import React, { useEffect, useMemo, useRef, useState } from "react";
import { heroSlides } from "../mock";
import { api } from "../lib/api";
import { MapPin, Calendar as CalendarIcon, Users, Search, ChevronLeft, ChevronRight, Building2, Compass, MapPinned } from "lucide-react";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";
import { useToast } from "../hooks/use-toast";

const HeroSearch = () => {
  const [index, setIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState(null); // {type: state|city|hotel, ...}
  const [hotels, setHotels] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [checkIn, setCheckIn] = useState();
  const [checkOut, setCheckOut] = useState();
  const [guests, setGuests] = useState({ adults: 2, children: 0, rooms: 1 });
  const { toast } = useToast();
  const wrapRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % heroSlides.length), 6000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.listHotels();
        setHotels(data);
      } catch (_) {}
    })();
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const slide = heroSlides[index];

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const states = new Map(); // state -> {country, cities:Map(city->hotels[])}
    for (const h of hotels) {
      const st = h.state || h.country || "Other";
      const ct = h.country || "";
      if (!states.has(st)) states.set(st, { state: st, country: ct, cities: new Map() });
      const bucket = states.get(st);
      const city = h.city || "—";
      if (!bucket.cities.has(city)) bucket.cities.set(city, []);
      bucket.cities.get(city).push(h);
    }

    const stateList = [];
    const cityList = [];
    const hotelList = [];
    for (const [st, bucket] of states) {
      const stateHotels = Array.from(bucket.cities.values()).flat();
      const stateMatch = !q || st.toLowerCase().includes(q) || bucket.country.toLowerCase().includes(q);
      const anyChildMatch = stateHotels.some((h) =>
        (h.name || "").toLowerCase().includes(q) ||
        (h.city || "").toLowerCase().includes(q) ||
        (h.brand || "").toLowerCase().includes(q)
      );
      if (stateMatch || anyChildMatch) {
        stateList.push({ type: "state", state: st, country: bucket.country, count: stateHotels.length });
      }
      for (const [ct, hs] of bucket.cities) {
        const cityMatch = !q || ct.toLowerCase().includes(q) || st.toLowerCase().includes(q);
        const cityHotelMatch = hs.some((h) => (h.name || "").toLowerCase().includes(q) || (h.brand || "").toLowerCase().includes(q));
        if (cityMatch || cityHotelMatch) {
          cityList.push({ type: "city", city: ct, state: st, country: bucket.country, count: hs.length });
        }
        for (const h of hs) {
          if (!q ||
            (h.name || "").toLowerCase().includes(q) ||
            (h.city || "").toLowerCase().includes(q) ||
            (h.state || "").toLowerCase().includes(q) ||
            (h.brand || "").toLowerCase().includes(q)
          ) {
            hotelList.push({ type: "hotel", hotel: h });
          }
        }
      }
    }
    return {
      states: stateList.slice(0, 6),
      cities: cityList.slice(0, 8),
      hotels: hotelList.slice(0, 10),
    };
  }, [hotels, query]);

  const applySelection = (sel) => {
    setSelection(sel);
    setShowDropdown(false);
    // Update input text
    if (sel.type === "state") setQuery(sel.state);
    if (sel.type === "city") setQuery(`${sel.city}, ${sel.state}`);
    if (sel.type === "hotel") setQuery(sel.hotel.name);
    // Emit event so PublicHotels filters accordingly
    const detail = sel.type === "hotel"
      ? { type: "hotel", hotel: sel.hotel }
      : sel.type === "city"
      ? { type: "city", city: sel.city, state: sel.state }
      : { type: "state", state: sel.state };
    window.dispatchEvent(new CustomEvent("aip:filter-hotels", { detail }));
    // Smooth scroll to hotels section
    setTimeout(() => document.getElementById("hotels")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const clearSelection = () => {
    setSelection(null);
    setQuery("");
    window.dispatchEvent(new CustomEvent("aip:filter-hotels", { detail: { type: "clear" } }));
  };

  const handleSearch = () => {
    if (!selection && !query) {
      toast({ title: "Where to?", description: "Type a state, city or hotel name to see suggestions." });
      return;
    }
    if (!selection && suggestions.hotels[0]) {
      applySelection(suggestions.hotels[0]);
      return;
    }
    toast({
      title: "Searching hotels",
      description: `${query} · ${checkIn ? format(checkIn, "d MMM") : "Any date"} → ${checkOut ? format(checkOut, "d MMM") : "Any date"} · ${guests.adults + guests.children} guests`,
    });
    document.getElementById("hotels")?.scrollIntoView({ behavior: "smooth" });
  };

  const hasAnyResults = suggestions.states.length + suggestions.cities.length + suggestions.hotels.length > 0;

  return (
    <section className="relative w-full h-[720px] overflow-hidden">
      {heroSlides.map((s, i) => (
        <div key={s.id} className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}>
          <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/20" />
        </div>
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-6 h-full flex flex-col justify-center pt-20">
        <div className="max-w-2xl text-white">
          <span className="inline-block text-[11px] tracking-[0.25em] font-medium mb-4 border-l-2 border-[#c8102e] pl-3">{slide.tag}</span>
          <h1 className="text-4xl md:text-6xl font-serif font-medium leading-[1.05] mb-5">{slide.title}</h1>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl leading-relaxed">{slide.subtitle}</p>
          <Button className="bg-white text-neutral-900 hover:bg-neutral-100 rounded-none px-6 py-6 text-sm font-medium tracking-wide">{slide.cta}</Button>
        </div>

        <div className="absolute bottom-52 left-6 md:left-12 flex items-center gap-4">
          <button onClick={() => setIndex((i) => (i - 1 + heroSlides.length) % heroSlides.length)} className="p-2 border border-white/40 text-white hover:bg-white hover:text-neutral-900 transition-colors" aria-label="Previous slide"><ChevronLeft size={18} /></button>
          <div className="text-white text-sm font-medium tabular-nums">{String(index + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}</div>
          <button onClick={() => setIndex((i) => (i + 1) % heroSlides.length)} className="p-2 border border-white/40 text-white hover:bg-white hover:text-neutral-900 transition-colors" aria-label="Next slide"><ChevronRight size={18} /></button>
        </div>
      </div>

      {/* Search Box */}
      <div className="absolute bottom-0 left-0 right-0 z-20">
        <div className="max-w-7xl mx-auto px-6 pb-8">
          <div className="bg-white shadow-2xl border border-neutral-100">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-neutral-200">
              {/* Destination search with dropdown */}
              <div className="p-5 relative" ref={wrapRef}>
                <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5 font-medium">Destination</label>
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#c8102e]" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); setSelection(null); }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="State, city or hotel"
                    className="w-full outline-none text-sm font-medium text-neutral-900 placeholder:text-neutral-400"
                  />
                  {selection && (
                    <button onClick={clearSelection} className="text-xs text-neutral-500 hover:text-[#c8102e]">Clear</button>
                  )}
                </div>

                {showDropdown && hasAnyResults && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-neutral-200 shadow-xl max-h-96 overflow-y-auto z-30">
                    {suggestions.states.length > 0 && (
                      <div>
                        <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-neutral-500">States</div>
                        {suggestions.states.map((s) => (
                          <button key={"s-" + s.state} onClick={() => applySelection(s)} className="w-full flex items-center justify-between text-left px-4 py-2 hover:bg-neutral-100">
                            <span className="flex items-center gap-2 text-sm text-neutral-900"><Compass size={14} className="text-[#c8102e]" />{s.state}<span className="text-xs text-neutral-500">{s.country}</span></span>
                            <span className="text-xs text-neutral-500">{s.count} hotel{s.count > 1 ? "s" : ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {suggestions.cities.length > 0 && (
                      <div className="border-t border-neutral-100">
                        <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-neutral-500">Cities</div>
                        {suggestions.cities.map((c) => (
                          <button key={"c-" + c.city + c.state} onClick={() => applySelection(c)} className="w-full flex items-center justify-between text-left px-4 py-2 hover:bg-neutral-100">
                            <span className="flex items-center gap-2 text-sm text-neutral-900"><MapPinned size={14} className="text-[#c8102e]" />{c.city}<span className="text-xs text-neutral-500">{c.state}</span></span>
                            <span className="text-xs text-neutral-500">{c.count} hotel{c.count > 1 ? "s" : ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {suggestions.hotels.length > 0 && (
                      <div className="border-t border-neutral-100">
                        <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-neutral-500">Hotels</div>
                        {suggestions.hotels.map((h) => (
                          <button key={"h-" + h.hotel.id} onClick={() => applySelection(h)} className="w-full flex items-center gap-3 text-left px-4 py-2 hover:bg-neutral-100">
                            <div className="w-10 h-10 bg-neutral-100 overflow-hidden flex-shrink-0">
                              {h.hotel.image && <img src={h.hotel.image} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-neutral-900 truncate">{h.hotel.name}</div>
                              <div className="text-xs text-neutral-500 truncate">{h.hotel.city}{h.hotel.state ? `, ${h.hotel.state}` : ""} · {h.hotel.brand}</div>
                            </div>
                            <Building2 size={14} className="text-neutral-400" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {showDropdown && !hasAnyResults && query && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-neutral-200 shadow-xl px-4 py-6 text-center text-sm text-neutral-500 z-30">
                    No matches. Try a different state, city or hotel name.
                  </div>
                )}
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-5 text-left hover:bg-neutral-50 transition-colors">
                    <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5 font-medium">Check-in</label>
                    <div className="flex items-center gap-2">
                      <CalendarIcon size={16} className="text-[#c8102e]" />
                      <span className="text-sm font-medium text-neutral-900">{checkIn ? format(checkIn, "EEE, d MMM yyyy") : "Select date"}</span>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={checkIn} onSelect={setCheckIn} initialFocus /></PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-5 text-left hover:bg-neutral-50 transition-colors">
                    <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5 font-medium">Check-out</label>
                    <div className="flex items-center gap-2">
                      <CalendarIcon size={16} className="text-[#c8102e]" />
                      <span className="text-sm font-medium text-neutral-900">{checkOut ? format(checkOut, "EEE, d MMM yyyy") : "Select date"}</span>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={checkOut} onSelect={setCheckOut} initialFocus /></PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <button className="p-5 text-left hover:bg-neutral-50 transition-colors">
                    <label className="block text-[11px] uppercase tracking-widest text-neutral-500 mb-1.5 font-medium">Guests & Rooms</label>
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-[#c8102e]" />
                      <span className="text-sm font-medium text-neutral-900">{guests.adults + guests.children} Guest{guests.adults + guests.children > 1 ? "s" : ""} · {guests.rooms} Room{guests.rooms > 1 ? "s" : ""}</span>
                    </div>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4">
                  {[
                    { key: "adults", label: "Adults", min: 1 },
                    { key: "children", label: "Children", min: 0 },
                    { key: "rooms", label: "Rooms", min: 1 },
                  ].map((row) => (
                    <div key={row.key} className="flex items-center justify-between py-2.5">
                      <span className="text-sm font-medium text-neutral-800">{row.label}</span>
                      <div className="flex items-center gap-3">
                        <button onClick={() => setGuests((g) => ({ ...g, [row.key]: Math.max(row.min, g[row.key] - 1) }))} className="w-8 h-8 border border-neutral-300 hover:border-[#c8102e] hover:text-[#c8102e] transition-colors">-</button>
                        <span className="w-6 text-center text-sm font-medium">{guests[row.key]}</span>
                        <button onClick={() => setGuests((g) => ({ ...g, [row.key]: g[row.key] + 1 }))} className="w-8 h-8 border border-neutral-300 hover:border-[#c8102e] hover:text-[#c8102e] transition-colors">+</button>
                      </div>
                    </div>
                  ))}
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-center justify-between px-5 py-3 bg-neutral-50 border-t border-neutral-200">
              <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                <input type="checkbox" className="accent-[#c8102e]" defaultChecked /> Use my AIP Rewards points
              </label>
              <Button onClick={handleSearch} className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-8 py-5 text-sm font-medium tracking-wide">
                <Search size={16} className="mr-2" /> Search Hotels
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
